import React from "react";
import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import CiroDropDown from "../../../shared/CiroDropdown";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import {
  FieldMappingCiroFieldEnum,
  IImportState,
  ImportStepEnum,
} from "./useImportState";

interface IMappingStepProps {
  importState: IImportState;
}

const MappingStep: React.FC<IMappingStepProps> = ({ importState }) => {
  const {
    setStep,
    csvHeaders,
    mappings,
    setMappings,
    error,
    setError,
    isProcessing,
  } = importState;

  const handleFieldMapping = (fieldIndex: number, csvField: string | null) => {
    const newMappings = [...mappings];
    newMappings[fieldIndex].csvField = csvField;
    setMappings(newMappings);
  };

  const validateContacts = () => {
    // Check if required fields are mapped
    const requiredMappings = mappings.filter((m) => m.required);
    const missingRequiredMappings = requiredMappings.filter((m) => !m.csvField);

    if (missingRequiredMappings.length > 0) {
      setError(
        `Missing required field mappings: ${missingRequiredMappings.map((m) => m.ciroField).join(", ")}`,
      );
      return false;
    }

    // Check that either Company Name or Contact LinkedIn URL is mapped
    const companyNameMapping = mappings.find(
      (m) => m.ciroField === FieldMappingCiroFieldEnum.COMPANY_NAME,
    );
    const contactLinkedInMapping = mappings.find(
      (m) => m.ciroField === FieldMappingCiroFieldEnum.CONTACT_LINKEDIN_URL,
    );

    if (
      (!companyNameMapping?.csvField || companyNameMapping.csvField === null) &&
      (!contactLinkedInMapping?.csvField ||
        contactLinkedInMapping.csvField === null)
    ) {
      setError("You must map either Company name or Contact LinkedIn URL");
      return false;
    }

    return true;
  };

  const handleValidateAndProceed = () => {
    if (validateContacts()) {
      setStep(ImportStepEnum.VALIDATION);
    }
  };

  return (
    <div className={classNames("p-6")}>
      <h2 className={classNames("text-xl", "font-medium", "mb-3")}>
        CSV fields mapping
      </h2>

      <p className={classNames("text-sm", "text-gray-600", "mb-2")}>
        Please indicate which fields you'd like to import into Ciro
      </p>
      <p className={classNames("text-xs", "text-gray-600", "mb-6")}>
        <span className="text-red-600">*</span> Required field &nbsp;&nbsp;
        <span className="text-red-600">†</span> Either Contact LinkedIn URL or
        Company name is required
      </p>

      <div>
        {/* Header row */}
        <div className={classNames("flex", "mb-3")}>
          <div className={classNames("font-medium", "text-gray-700", "flex-1")}>
            Ciro fields
          </div>
          <div className={classNames("w-8")}></div>
          <div className={classNames("font-medium", "text-gray-700", "flex-1")}>
            CSV columns
          </div>
        </div>

        <div className={classNames("overflow-y-auto", "space-y-3")}>
          {mappings.map((mapping, index) => (
            <div
              key={mapping.ciroField}
              className={classNames("flex", "items-center", {
                "pb-16": index === mappings.length - 1,
              })}
            >
              {/* Ciro Field */}
              <div className={classNames("flex-1", "pr-2")}>
                <div
                  className={classNames(
                    "w-full",
                    "border",
                    "border-gray-300",
                    "rounded-md",
                    "py-2",
                    "px-3",
                    "bg-gray-50",
                    "text-sm",
                  )}
                >
                  <span>
                    {mapping.ciroField}
                    {mapping.required && (
                      <span className="text-red-600 ml-1">*</span>
                    )}
                    {(mapping.ciroField === "Contact LinkedIn URL" ||
                      mapping.ciroField === "Company name") && (
                      <span className="text-red-600 ml-1">†</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Arrow */}
              <div className={classNames("w-8", "flex", "justify-center")}>
                <ArrowRightIcon
                  className={classNames("h-4", "w-4", "text-orange-500")}
                  aria-hidden="true"
                />
              </div>

              {/* CSV Column dropdown */}
              <div className={classNames("flex-1", "pl-2")}>
                <CiroDropDown
                  maxMenuHeight={index === mappings.length - 1 ? 50 : 100}
                  value={mapping.csvField || ""}
                  onChange={(value) => handleFieldMapping(index, value || null)}
                  options={[
                    ...csvHeaders.map((header) => ({
                      value: header,
                      label: header,
                    })),
                  ]}
                  error={
                    mapping.required && !mapping.csvField
                      ? "This field is required"
                      : undefined
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div
          className={classNames(
            "p-3",
            "bg-red-50",
            "text-red-600",
            "text-sm",
            "rounded-md",
          )}
        >
          {error}
        </div>
      )}

      <div className={classNames("flex", "justify-end", "gap-2")}>
        <CiroButton
          analyticsField={"Back to upload"}
          onClick={() => setStep(ImportStepEnum.UPLOAD)}
          style={CiroButtonStyleEnum.INVERTED_LOUD}
        >
          Back
        </CiroButton>
        <CiroButton
          analyticsField={"Validate contacts"}
          onClick={handleValidateAndProceed}
          style={CiroButtonStyleEnum.LOUD}
          disabled={isProcessing}
        >
          Validate Contacts
        </CiroButton>
      </div>
    </div>
  );
};

export default MappingStep;
